import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyCell,
  Input,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Pencil,
  Search,
  MailX,
  Send,
  UserCheck,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import { FeatureFlag } from '../../constants/featureFlags';
import { useStore } from '../../store';
import { UnclaimedClinicianName } from '../clinician/UnclaimedClinicianName';
import { MemberStatusFilter } from './MemberStatusFilter';
import { MEMBER_STATUS_ORDER } from './memberStatus';
import type { MemberStatus, OrgMemberResponse, RoleInfo } from './types';
import {
  getMemberHealthIssues,
  MEMBER_HEALTH_EXPLANATIONS,
  MEMBER_HEALTH_LABELS,
} from './memberHealth';
import type { RogerDocClinicianAccount } from '../../services/accountLinkApi';
import { filterUserVisibleRoles } from '../../utils/rbacRoles';
import { formatDateTimeInAgencyZone } from '../../utils/dateUtils';
import { PhoneLink } from '../PhoneLink';
import { useMediaQuery } from '../../hooks/useIsMobile';

type SortKey = 'name' | 'email' | 'lastLogin';
type SortDirection = 'asc' | 'desc';

type SortConfig = {
  key: SortKey;
  direction: SortDirection;
};

const DEFAULT_SORT: SortConfig = { key: 'name', direction: 'asc' };
const SORT_STORAGE_KEY = 'clinician-manager-members-sort';

const SORT_LABELS: Record<SortKey, string> = {
  name: 'Name',
  email: 'Email',
  lastLogin: 'Last Login',
};

const INVITE_BADGE_STYLES = {
  expired: 'border-amber-300 bg-amber-50 text-amber-800',
  pending: 'border-blue-300 bg-blue-50 text-blue-800',
} as const;

function isSortKey(value: unknown): value is SortKey {
  // A saved sort on a column that has since moved into the row details falls
  // back to the default rather than ordering the table invisibly.
  return value === 'name' || value === 'email' || value === 'lastLogin';
}

function isSortDirection(value: unknown): value is SortDirection {
  return value === 'asc' || value === 'desc';
}

function readInitialSort(): SortConfig {
  if (typeof window === 'undefined') {
    return DEFAULT_SORT;
  }

  try {
    const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_SORT;
    }

    const parsed = JSON.parse(stored) as Partial<SortConfig>;
    if (isSortKey(parsed.key) && isSortDirection(parsed.direction)) {
      return { key: parsed.key, direction: parsed.direction };
    }
  } catch {
    // Invalid stored preferences should not block the table from rendering.
  }

  return DEFAULT_SORT;
}

function normalized(value: string | null | undefined): string {
  return (value ?? '').trim().toLocaleLowerCase();
}

function memberName(member: OrgMemberResponse): string {
  return normalized(
    [member.first_name, member.last_name].filter(Boolean).join(' ')
  );
}

function lastLoginSortValue(member: OrgMemberResponse): number {
  if (member.is_pending || !member.last_login) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = Date.parse(member.last_login);
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

/**
 * The group a member sorts into: 0 for everybody who can still get in, 1 for a
 * deactivated membership. Normalized to those two values so the difference is
 * a valid comparator result rather than whatever the timestamps subtract to.
 */
function deactivationRank(member: OrgMemberResponse): number {
  return member.deactivated_at ? 1 : 0;
}

function compareMembers(
  a: OrgMemberResponse,
  b: OrgMemberResponse,
  sort: SortConfig
): number {
  // Deactivated members sit under everybody else whichever column is sorted.
  // This is deliberately ahead of the column comparison and outside the
  // direction flip below: reversing the sort reverses the order inside each
  // block, it does not lift the deactivated block back to the top.
  const groupResult = deactivationRank(a) - deactivationRank(b);
  if (groupResult !== 0) {
    return groupResult;
  }

  let result = 0;

  if (sort.key === 'name') {
    result = compareStrings(memberName(a), memberName(b));
  } else if (sort.key === 'email') {
    result = compareStrings(normalized(a.email), normalized(b.email));
  } else {
    const aLastLogin = lastLoginSortValue(a);
    const bLastLogin = lastLoginSortValue(b);
    result =
      aLastLogin === Number.NEGATIVE_INFINITY &&
      bLastLogin === Number.NEGATIVE_INFINITY
        ? 0
        : aLastLogin - bLastLogin;
  }

  if (result !== 0) {
    return sort.direction === 'asc' ? result : -result;
  }

  return (
    compareStrings(memberName(a), memberName(b)) ||
    compareStrings(normalized(a.email), normalized(b.email)) ||
    compareStrings(a.workos_user_id, b.workos_user_id)
  );
}

function sortMembers(
  members: OrgMemberResponse[],
  sort: SortConfig
): OrgMemberResponse[] {
  return members
    .map((member, index) => ({ member, index }))
    .sort((a, b) => {
      const result = compareMembers(a.member, b.member, sort);
      return result || a.index - b.index;
    })
    .map(({ member }) => member);
}

const SortIcon: React.FC<{
  direction: SortDirection | null;
}> = React.memo(function SortIcon({ direction }) {
  if (direction === 'asc') {
    return <ArrowUp className='h-3.5 w-3.5' aria-hidden='true' />;
  }
  if (direction === 'desc') {
    return <ArrowDown className='h-3.5 w-3.5' aria-hidden='true' />;
  }
  return <ArrowUpDown className='h-3.5 w-3.5' aria-hidden='true' />;
});

const SortableTableHead: React.FC<{
  sortKey: SortKey;
  sort: SortConfig;
  onSort: (key: SortKey) => void;
  title?: string;
  className?: string;
}> = React.memo(function SortableTableHead({
  sortKey,
  sort,
  onSort,
  title,
  className,
}) {
  const label = SORT_LABELS[sortKey];
  const isActive = sort.key === sortKey;
  const direction = isActive ? sort.direction : null;

  return (
    <TableHead
      className={className}
      title={title}
      aria-label={label}
      aria-sort={
        isActive
          ? sort.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      <button
        type='button'
        className='inline-flex h-8 items-center gap-1 whitespace-nowrap rounded text-left font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        aria-label={`Sort by ${label}`}
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        <SortIcon direction={direction} />
      </button>
    </TableHead>
  );
});

// ─── Badge lists ─────────────────────────────────────────────────────────────

type BadgeLabel = { key: string; label: string };

/**
 * A list of badges. Collapsed, it shows the first one and a "+N" count, on one
 * line, so every row in the table is the same height; the row's expanded
 * details show the whole list.
 */
const BadgeList: React.FC<{
  labels: BadgeLabel[];
  collapsed: boolean;
  variant?: 'outline' | 'secondary';
}> = React.memo(function BadgeList({ labels, collapsed, variant = 'outline' }) {
  if (labels.length === 0) {
    return <EmptyCell />;
  }
  const shown = collapsed ? labels.slice(0, 1) : labels;
  const hiddenCount = labels.length - shown.length;
  return (
    <div
      className={
        collapsed
          ? 'flex items-center gap-1 whitespace-nowrap'
          : 'flex flex-wrap gap-1'
      }
    >
      {shown.map(item => (
        <Badge
          key={item.key}
          variant={variant}
          className='max-w-[11rem] text-xs whitespace-nowrap'
          title={item.label}
        >
          <span className='truncate'>{item.label}</span>
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <Badge variant='secondary' className='text-xs whitespace-nowrap'>
          +{hiddenCount}
        </Badge>
      )}
    </div>
  );
});

/**
 * One label per clinic the user can access (`clinic_access` — the set the
 * backend clinic filter enforces), annotated with the assignable roles held
 * there. Access without roles is just the clinic name, which is how a
 * back-office user with agency-wide access legitimately looks.
 */
function clinicAccessLabels(
  access: OrgMemberResponse['clinic_access'],
  assignments: OrgMemberResponse['clinic_assignments'],
  hiddenClinicIds?: ReadonlySet<string>,
  clinicNamesById?: ReadonlyMap<string, string>
): BadgeLabel[] {
  const assignmentByClinicId = new Map(
    (assignments ?? []).map(assignment => [assignment.clinic_id, assignment])
  );
  return (access ?? [])
    .filter(entry => !hiddenClinicIds?.has(entry.clinic_id))
    .map(entry => {
      const assignment = assignmentByClinicId.get(entry.clinic_id);
      const roles: string[] = [];
      if (assignment?.is_assignable_clinician) roles.push('clinician');
      if (assignment?.is_case_manager) roles.push('case manager');
      // The access entry names the clinic itself, and that name is blank for
      // an unnamed clinic. The clinic list is the fresher source, so it wins
      // outright here rather than only filling in for a blank.
      const clinicName =
        clinicNamesById?.get(entry.clinic_id) ?? entry.clinic_name;
      return {
        key: entry.clinic_id,
        label:
          roles.length > 0
            ? `${clinicName} · ${roles.join(' / ')}`
            : clinicName,
      };
    });
}

function roleLabels(roles: RoleInfo[] | undefined): BadgeLabel[] {
  return filterUserVisibleRoles(roles ?? []).map(role => ({
    key: role.id,
    label: role.name,
  }));
}

function appAccountLabels(
  accounts: RogerDocClinicianAccount[] | undefined
): BadgeLabel[] {
  return (accounts ?? []).map(account => ({
    key: account.id,
    label: account.username,
  }));
}

// ─── LastLoginCell ───────────────────────────────────────────────────────────

const LastLoginCell: React.FC<{
  isPending: boolean;
  lastLogin: string | null | undefined;
}> = React.memo(function LastLoginCell({ isPending, lastLogin }) {
  // A pending invitee hasn't accepted THIS agency's invite yet — the Name cell's
  // Pending badge already carries that state, so we mask any last_login with the
  // table's em-dash. last_login is now scoped to the current agency, but a member
  // pending here may still have a non-null last_login from another agency, so this
  // deliberately keys on is_pending first.
  if (isPending) {
    return <EmptyCell />;
  }
  // Accepted but never signed in: the row GTM most needs to spot.
  if (!lastLogin) {
    return <span>Never</span>;
  }
  return <span>{formatDateTimeInAgencyZone(lastLogin)}</span>;
});

// ─── PhoneCell ───────────────────────────────────────────────────────────────

const PhoneCell: React.FC<{ phone: string | null | undefined }> = React.memo(
  function PhoneCell({ phone }) {
    // Phone is optional on a user, so an unset one gets the table's blank cell
    // rather than PhoneLink's own "N/A" text.
    if (!phone?.trim()) {
      return <EmptyCell />;
    }
    return (
      <PhoneLink
        phone={phone}
        className='text-sm'
        // On mobile the number is a tel: link inside a row whose own click
        // shows or hides its details. Placing a call should not also toggle
        // the row; the plain-text desktop rendering is left to bubble so the
        // whole row stays clickable there.
        onClick={e => e.stopPropagation()}
      />
    );
  }
);

// ─── MemberActionsMenu ───────────────────────────────────────────────────────

/**
 * The row's actions behind one "..." button, so the column stays narrow.
 *
 * Clicks stop here: React events bubble out of the menu's portal to the row,
 * whose own click shows or hides its details.
 */
const MemberActionsMenu: React.FC<{
  member: OrgMemberResponse;
  displayName: string;
  showResendInvite: boolean;
  isResendingInvite: boolean;
  onResendInvite: (member: OrgMemberResponse) => void;
  showRetry: boolean;
  isRetryingDeactivation: boolean;
  onRetryDeactivation: (member: OrgMemberResponse) => void;
  showToggleActivation: boolean;
  isDeactivated: boolean;
  isUpdatingActivation: boolean;
  onToggleActivation: (member: OrgMemberResponse) => void;
  showCancelInvite: boolean;
  isCancellingInvite: boolean;
  onCancelInvite: (member: OrgMemberResponse) => void;
}> = React.memo(function MemberActionsMenu({
  member,
  displayName,
  showResendInvite,
  isResendingInvite,
  onResendInvite,
  showRetry,
  isRetryingDeactivation,
  onRetryDeactivation,
  showToggleActivation,
  isDeactivated,
  isUpdatingActivation,
  onToggleActivation,
  showCancelInvite,
  isCancellingInvite,
  onCancelInvite,
}) {
  const isBusy =
    isResendingInvite ||
    isRetryingDeactivation ||
    isUpdatingActivation ||
    isCancellingInvite;
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          aria-label={`Actions for ${displayName || member.email}`}
          onClick={stop}
          onPointerDown={stop}
        >
          {isBusy ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <MoreHorizontal className='h-4 w-4' />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' onClick={stop}>
        {showResendInvite && (
          <DropdownMenuItem
            disabled={isResendingInvite}
            onSelect={() => onResendInvite(member)}
          >
            <Send className='mr-2 h-4 w-4' />
            Resend invite
          </DropdownMenuItem>
        )}
        {showRetry && (
          <DropdownMenuItem
            disabled={isRetryingDeactivation}
            onSelect={() => onRetryDeactivation(member)}
          >
            <UserMinus className='mr-2 h-4 w-4' />
            Retry deactivation
          </DropdownMenuItem>
        )}
        {showToggleActivation && (
          <DropdownMenuItem
            disabled={isUpdatingActivation}
            onSelect={() => onToggleActivation(member)}
          >
            {isDeactivated ? (
              <UserCheck className='mr-2 h-4 w-4' />
            ) : (
              <UserMinus className='mr-2 h-4 w-4' />
            )}
            {isDeactivated ? 'Reactivate' : 'Deactivate'}
          </DropdownMenuItem>
        )}
        {showCancelInvite && (
          <DropdownMenuItem
            disabled={isCancellingInvite}
            onSelect={() => onCancelInvite(member)}
          >
            <MailX className='mr-2 h-4 w-4' />
            Cancel invite
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

// ─── MemberRow ───────────────────────────────────────────────────────────────

type MemberRowProps = {
  member: OrgMemberResponse;
  onClick: (member: OrgMemberResponse) => void;
  showClinicInfoInUserManager: boolean;
  // Below desktop width some columns are hidden and shown in the row's
  // details instead, so every row has something to expand.
  compact: boolean;
  hiddenClinicIds?: ReadonlySet<string>;
  clinicNamesById?: ReadonlyMap<string, string>;
  rogerDocAccounts: RogerDocClinicianAccount[] | undefined;
  rogerDocAccountsLoaded: boolean;
  canResendInvite: boolean;
  isResendingInvite: boolean;
  onResendInvite: (member: OrgMemberResponse) => void;
  canDeactivate: boolean;
  isUpdatingActivation: boolean;
  onToggleActivation: (member: OrgMemberResponse) => void;
  canRetryDeactivation: boolean;
  isRetryingDeactivation: boolean;
  onRetryDeactivation: (member: OrgMemberResponse) => void;
  currentUserId?: string;
  canCancelInvite: boolean;
  isCancellingInvite: boolean;
  onCancelInvite: (member: OrgMemberResponse) => void;
};

const MemberRow: React.FC<MemberRowProps> = React.memo(function MemberRow({
  member,
  onClick,
  showClinicInfoInUserManager,
  compact,
  hiddenClinicIds,
  clinicNamesById,
  rogerDocAccounts,
  rogerDocAccountsLoaded,
  canResendInvite,
  isResendingInvite,
  onResendInvite,
  canDeactivate,
  isUpdatingActivation,
  onToggleActivation,
  canRetryDeactivation,
  isRetryingDeactivation,
  onRetryDeactivation,
  currentUserId,
  canCancelInvite,
  isCancellingInvite,
  onCancelInvite,
}) {
  const isDeactivated = Boolean(member.deactivated_at);
  const isSelf = !!currentUserId && member.roger_user_id === currentUserId;
  // Offered only to somebody who never arrived. Once they have signed in the
  // invitation is spent, and taking their access away is deactivation's job.
  const canRetractInvite =
    canCancelInvite && !!member.roger_user_id && !isSelf && !!member.is_pending;
  const healthIssues = getMemberHealthIssues(member, {
    appAccountsLoaded: rogerDocAccountsLoaded,
    // An inactive account leaves them just as unable to document a visit as no
    // account at all, so counting one would hide the problem this badge exists
    // to surface.
    hasAppAccount: (rogerDocAccounts ?? []).some(account => account.active),
  });
  const [expanded, setExpanded] = useState(false);
  const roles = roleLabels(member.role_assignments);
  const clinics = showClinicInfoInUserManager
    ? clinicAccessLabels(
        member.clinic_access,
        member.clinic_assignments,
        hiddenClinicIds,
        clinicNamesById
      )
    : [];
  const appAccounts = appAccountLabels(rogerDocAccounts);
  // Only offered when the one-line row is hiding something.
  const hasDetails =
    compact ||
    roles.length > 1 ||
    clinics.length > 1 ||
    appAccounts.length > 0 ||
    !!member.phone_number ||
    healthIssues.length > 1;
  const columnCount = showClinicInfoInUserManager ? 7 : 6;
  const displayName = [member.first_name, member.last_name]
    .filter(Boolean)
    .join(' ');
  const inviteBadgeLabel =
    member.invite_status === 'expired' ? 'Expired' : 'Pending';
  const inviteBadgeClassName =
    member.invite_status === 'expired'
      ? INVITE_BADGE_STYLES.expired
      : INVITE_BADGE_STYLES.pending;

  return (
    <>
      <TableRow
        // The tint has to be the undiluted value: an active row's own hover is
        // bg-muted/50, so any diluted mix lands within a unit or two of it and
        // the two states read as each other. --muted resolves to exactly
        // slate-100, so bg-muted would paint the same pixel here; slate is named
        // explicitly because the hover step has no token (--accent is --muted),
        // and a rest and hover pair is easier to reason about taken from one
        // scale. The badges in this file are hardcoded from the same scale.
        //
        // The hover half is not decoration. TableRow composes className through
        // tailwind-merge, so naming a hover background here drops the row's own
        // hover:bg-muted/50; omitting it would leave that rule to repaint the row
        // under the cursor and erase the tint just as somebody points at it.
        className={[
          isDeactivated ? 'bg-slate-100 hover:bg-slate-200' : '',
          hasDetails ? 'cursor-pointer' : '',
        ].join(' ')}
        // Clicking the row shows or hides its details. Editing is the pencil
        // button.
        onClick={hasDetails ? () => setExpanded(open => !open) : undefined}
      >
        <TableCell className='w-8 pr-0'>
          {hasDetails && (
            <button
              type='button'
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Hide' : 'Show'} all roles and clinics for ${displayName || member.email}`}
              className='flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              // The row's own click toggles too, so this one must stop here.
              onClick={e => {
                e.stopPropagation();
                setExpanded(open => !open);
              }}
            >
              {expanded ? (
                <ChevronDown className='h-4 w-4' />
              ) : (
                <ChevronRight className='h-4 w-4' />
              )}
            </button>
          )}
        </TableCell>

        <TableCell className='font-medium'>
          <div className='flex flex-wrap items-center gap-x-2 gap-y-1 lg:flex-nowrap lg:whitespace-nowrap'>
            {/* Unclaimed imported staff stay listed here — this is where an
              operator resolves them — so the row carries the same amber
              treatment the assignment surfaces use, in this surface's words. */}
            <UnclaimedClinicianName
              name={displayName}
              unclaimed={member.unclaimed_import === true}
              variant='directory'
              testId='org-member-unclaimed'
            />
            {healthIssues.length > 0 && (
              // When the label collapses to a count, the problems are listed as
              // real text in the row's expanded details: a native title
              // attribute is mouse-only and would never reach a keyboard or
              // touch user.
              <Badge
                variant='outline'
                className='w-fit shrink-0 whitespace-nowrap text-xs border-amber-300 bg-amber-50 text-amber-800'
              >
                <AlertTriangle className='mr-1 h-3 w-3' />
                {healthIssues.length === 1
                  ? MEMBER_HEALTH_LABELS[healthIssues[0]]
                  : `${healthIssues.length} setup problems`}
              </Badge>
            )}
            {isDeactivated && (
              // They keep their roles, clinic access and history, so nothing else
              // on the row says they cannot get in. This badge is the only place
              // that shows it.
              <Badge
                variant='outline'
                // White, not the row's own slate: the row it sits on is tinted
                // from the same scale, and matching it would leave the badge
                // defined by its border alone and invert it under the cursor.
                className='shrink-0 whitespace-nowrap border-slate-300 bg-white text-xs text-slate-700'
                data-testid='org-member-deactivated'
              >
                Deactivated
              </Badge>
            )}
            {member.is_pending && (
              <>
                <Badge
                  variant='outline'
                  className={`shrink-0 whitespace-nowrap text-xs ${inviteBadgeClassName}`}
                >
                  {inviteBadgeLabel}
                </Badge>
              </>
            )}
          </div>
        </TableCell>

        <TableCell className='text-muted-foreground'>
          <div
            className='max-w-[9rem] truncate lg:max-w-[12rem] xl:max-w-[14rem]'
            title={member.email}
          >
            {member.email}
          </div>
        </TableCell>

        <TableCell className='hidden whitespace-nowrap text-muted-foreground xl:table-cell'>
          <LastLoginCell
            isPending={member.is_pending ?? false}
            lastLogin={member.last_login}
          />
        </TableCell>

        <TableCell className='hidden lg:table-cell'>
          <BadgeList labels={roles} collapsed />
        </TableCell>

        {showClinicInfoInUserManager && (
          <TableCell className='hidden xl:table-cell'>
            <BadgeList labels={clinics} collapsed />
          </TableCell>
        )}

        <TableCell className='w-20'>
          <div className='flex justify-end'>
            <Button
              variant='ghost'
              size='sm'
              aria-label={`Edit ${displayName || member.email}`}
              onClick={e => {
                e.stopPropagation();
                onClick(member);
              }}
            >
              <Pencil className='h-4 w-4' />
            </Button>
            <MemberActionsMenu
              member={member}
              displayName={displayName}
              showResendInvite={canResendInvite && !!member.is_pending}
              isResendingInvite={isResendingInvite}
              onResendInvite={onResendInvite}
              // Not offered on your own row: the route refuses it, and the generic
              // failure toast would read as "try again" for something that can never
              // work.
              showRetry={
                canRetryDeactivation &&
                !!member.roger_user_id &&
                !isSelf &&
                member.deactivation_status === 'workos_removal_pending'
              }
              isRetryingDeactivation={isRetryingDeactivation}
              onRetryDeactivation={onRetryDeactivation}
              // Not offered alongside Cancel invite either. For somebody who never
              // arrived, deactivating is the wrong tool and looks like the right one: it
              // leaves the invitation live, so they can still complete signup and land in
              // an account that refuses them. Reactivate is not hidden the same way:
              // somebody put on hold before they ever arrived still has to be able to
              // come off it.
              showToggleActivation={
                canDeactivate &&
                !!member.roger_user_id &&
                !isSelf &&
                (!canRetractInvite || isDeactivated)
              }
              isDeactivated={isDeactivated}
              isUpdatingActivation={isUpdatingActivation}
              onToggleActivation={onToggleActivation}
              showCancelInvite={canRetractInvite}
              isCancellingInvite={isCancellingInvite}
              onCancelInvite={onCancelInvite}
            />
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow
          className={
            isDeactivated
              ? 'bg-slate-100 hover:bg-slate-100'
              : 'bg-muted/30 hover:bg-muted/30'
          }
        >
          <TableCell />
          <TableCell colSpan={columnCount - 1}>
            <div className='grid gap-4 py-1 sm:grid-cols-2 lg:grid-cols-5'>
              <MemberDetail title='Last login' className='xl:hidden'>
                <div className='text-muted-foreground'>
                  <LastLoginCell
                    isPending={member.is_pending ?? false}
                    lastLogin={member.last_login}
                  />
                </div>
              </MemberDetail>
              <MemberDetail title='Phone'>
                <PhoneCell phone={member.phone_number} />
              </MemberDetail>
              <MemberDetail title='Agency roles'>
                <BadgeList labels={roles} collapsed={false} />
              </MemberDetail>
              {showClinicInfoInUserManager && (
                <MemberDetail title='Clinic access'>
                  <BadgeList labels={clinics} collapsed={false} />
                </MemberDetail>
              )}
              <MemberDetail title='Roger App user'>
                <BadgeList
                  labels={appAccounts}
                  collapsed={false}
                  variant='secondary'
                />
              </MemberDetail>
              {healthIssues.length > 0 && (
                <MemberDetail title='Setup problems'>
                  <ul className='list-disc pl-4 text-xs text-amber-800'>
                    {healthIssues.map(issue => (
                      <li key={issue}>{MEMBER_HEALTH_EXPLANATIONS[issue]}</li>
                    ))}
                  </ul>
                </MemberDetail>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

const MemberDetail: React.FC<{
  title: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, className, children }) => (
  <div className={className}>
    <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
      {title}
    </p>
    {children}
  </div>
);

// ─── MembersTable ────────────────────────────────────────────────────────────

type MembersTableProps = {
  members: OrgMemberResponse[];
  isLoading: boolean;
  hasSearchQuery: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: MemberStatus[];
  onStatusFilterChange: (value: MemberStatus[]) => void;
  /** Whether the rows shown fell back past the filter to answer a search. */
  showingOutsideFilter?: boolean;
  onEditMember: (member: OrgMemberResponse) => void;
  onInvite: () => void;
  onResendInvite: (member: OrgMemberResponse) => Promise<void> | void;
  canInvite: boolean;
  rogerDocLinksByEmail?: Record<string, RogerDocClinicianAccount[]>;
  /** Whether the Roger App account list loaded; false means "we could not ask". */
  rogerDocAccountsLoaded?: boolean;
  hiddenClinicIds?: ReadonlySet<string>;
  /** Resolved clinic display names, keyed by clinic id. */
  clinicNamesById?: ReadonlyMap<string, string>;
  canDeactivate?: boolean;
  onToggleActivation?: (member: OrgMemberResponse) => Promise<void> | void;
  onRetryDeactivation?: (member: OrgMemberResponse) => Promise<void> | void;
  currentUserId?: string;
  canCancelInvite?: boolean;
  onCancelInvite?: (member: OrgMemberResponse) => Promise<void> | void;
};

export const MembersTable: React.FC<MembersTableProps> = React.memo(
  function MembersTable({
    members,
    isLoading,
    hasSearchQuery,
    searchQuery,
    onSearchChange,
    statusFilter,
    onStatusFilterChange,
    showingOutsideFilter = false,
    onEditMember,
    onInvite,
    onResendInvite,
    canInvite,
    rogerDocLinksByEmail = {},
    // Defaults to false so an absent prop is read as "we could not ask" rather
    // than "nobody has an account", which would flag every clinician at once.
    rogerDocAccountsLoaded = false,
    hiddenClinicIds,
    clinicNamesById,
    canDeactivate = false,
    onToggleActivation,
    onRetryDeactivation,
    currentUserId,
    canCancelInvite = false,
    onCancelInvite,
  }) {
    const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
    const showClinicInfoInUserManager = hasFeatureFlag(
      FeatureFlag.SHOW_CLINIC_INFO_IN_USER_MANAGER
    );

    // Matches the xl: breakpoint the hidden columns come back at.
    const isCompact = useMediaQuery('(max-width: 1279px)');

    const [sort, setSort] = useState<SortConfig>(readInitialSort);

    useEffect(() => {
      try {
        window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
      } catch {
        // Sorting still works if localStorage is unavailable.
      }
    }, [sort]);

    const handleSort = useCallback((key: SortKey) => {
      setSort(prev => ({
        key,
        direction:
          prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
      }));
    }, []);

    const sortedMembers = useMemo(
      () => sortMembers(members, sort),
      [members, sort]
    );

    // A caption is the table's accessible name, so it is read out on entry and
    // in a list of tables. The ordering rule earns that place only on a roster
    // that actually has somebody deactivated in it.
    const hasDeactivatedMembers = useMemo(
      () => members.some(member => Boolean(member.deactivated_at)),
      [members]
    );

    // Track which pending members' invites are currently being resent so we can
    // show a per-row spinner and prevent duplicate clicks. A set (not a single
    // value) so overlapping resends on different rows stay independent.
    const [resendingEmails, setResendingEmails] = useState<Set<string>>(
      new Set()
    );
    const handleResendInvite = useCallback(
      async (member: OrgMemberResponse) => {
        setResendingEmails(prev => new Set(prev).add(member.email));
        try {
          await onResendInvite(member);
        } finally {
          setResendingEmails(prev => {
            const next = new Set(prev);
            next.delete(member.email);
            return next;
          });
        }
      },
      [onResendInvite]
    );

    // Mirrors the resend-invite set: per row, so two overlapping changes on
    // different people stay independent.
    const [updatingActivationIds, setUpdatingActivationIds] = useState<
      Set<string>
    >(new Set());
    const [retryingDeactivationIds, setRetryingDeactivationIds] = useState<
      Set<string>
    >(new Set());
    const [cancellingInviteIds, setCancellingInviteIds] = useState<Set<string>>(
      new Set()
    );
    const handleRetryDeactivation = useCallback(
      async (member: OrgMemberResponse) => {
        if (!onRetryDeactivation || !member.roger_user_id) return;
        const id = member.roger_user_id;
        setRetryingDeactivationIds(prev => new Set(prev).add(id));
        try {
          await onRetryDeactivation(member);
        } finally {
          setRetryingDeactivationIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      },
      [onRetryDeactivation]
    );

    const handleCancelInvite = useCallback(
      async (member: OrgMemberResponse) => {
        if (!onCancelInvite || !member.roger_user_id) return;
        const id = member.roger_user_id;
        setCancellingInviteIds(prev => new Set(prev).add(id));
        try {
          await onCancelInvite(member);
        } finally {
          setCancellingInviteIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      },
      [onCancelInvite]
    );

    const handleToggleActivation = useCallback(
      async (member: OrgMemberResponse) => {
        if (!onToggleActivation || !member.roger_user_id) return;
        const id = member.roger_user_id;
        setUpdatingActivationIds(prev => new Set(prev).add(id));
        try {
          await onToggleActivation(member);
        } finally {
          setUpdatingActivationIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      },
      [onToggleActivation]
    );

    return (
      <>
        {/* Header */}
        <h1 className='text-2xl font-bold tracking-tight'>User Manager</h1>

        {/* Filters -- kept in one row directly above the table, so the search,
            the status filter and Invite User all sit next to what they act on. */}
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center'>
          <div className='flex-1'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              {/* No visible label, so the filter row stays one line. The
                  field still needs a name for screen readers. */}
              <Input
                id='member-search'
                aria-label='Search users by name, email, or phone'
                placeholder='Search by name, email, or phone...'
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                className='w-full pl-9'
              />
            </div>
          </div>
          <div className='w-full lg:w-64'>
            <MemberStatusFilter
              value={statusFilter}
              onChange={onStatusFilterChange}
            />
          </div>
          {canInvite && (
            <Button onClick={onInvite} disabled={isLoading}>
              <UserPlus className='h-4 w-4' />
              Invite User
            </Button>
          )}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            <span className='ml-3 text-muted-foreground'>
              Loading organization members...
            </span>
          </div>
        ) : members.length === 0 ? (
          <div className='text-center text-muted-foreground py-8'>
            <p>
              {hasSearchQuery
                ? 'No members match your search.'
                : statusFilter.length < MEMBER_STATUS_ORDER.length
                  ? 'No members match the current filter.'
                  : 'No organization members found.'}
            </p>
          </div>
        ) : (
          <div className='space-y-2'>
            {showingOutsideFilter && (
              <p
                className='text-sm text-muted-foreground'
                data-testid='members-outside-filter-note'
              >
                Some matches are outside the selected statuses, so they are
                shown too.
              </p>
            )}
            <div className='rounded-md border'>
              {/* Below xl the Last Login, role and clinic columns drop into
                each row's details, so the table fits without scrolling
                sideways. On desktop the min-width keeps the badge columns
                from being starved of width and wrapping into tall rows. */}
              <Table className='xl:min-w-[920px]'>
                {/* The sortable headers announce their column through aria-sort,
                  but rows are ordered by deactivation first and that column
                  second. For anybody reading the table through aria-sort alone
                  that is the whole of the order, so the rule is stated here.
                  A caption has to be the table's first child, and sr-only sets
                  a negative margin the design system's own mt-2 would survive,
                  so that is cleared by name. */}
                <TableCaption className='sr-only mt-0'>
                  {hasDeactivatedMembers
                    ? 'Organization members. Deactivated members are listed last.'
                    : 'Organization members.'}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-8 pr-0'>
                      <span className='sr-only'>Details</span>
                    </TableHead>
                    <SortableTableHead
                      sortKey='name'
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableTableHead
                      sortKey='email'
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortableTableHead
                      sortKey='lastLogin'
                      sort={sort}
                      onSort={handleSort}
                      title='Last login to this agency'
                      className='hidden xl:table-cell'
                    />
                    <TableHead className='hidden whitespace-nowrap lg:table-cell'>
                      Agency Roles
                    </TableHead>
                    {showClinicInfoInUserManager && (
                      <TableHead className='hidden whitespace-nowrap xl:table-cell'>
                        Clinic Access
                      </TableHead>
                    )}
                    <TableHead className='sr-only'>Access</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedMembers.map(member => (
                    <MemberRow
                      key={member.workos_user_id}
                      member={member}
                      onClick={onEditMember}
                      showClinicInfoInUserManager={showClinicInfoInUserManager}
                      compact={isCompact}
                      hiddenClinicIds={hiddenClinicIds}
                      clinicNamesById={clinicNamesById}
                      rogerDocAccounts={
                        rogerDocLinksByEmail[member.email.toLowerCase()]
                      }
                      rogerDocAccountsLoaded={rogerDocAccountsLoaded}
                      canResendInvite={canInvite}
                      isResendingInvite={resendingEmails.has(member.email)}
                      onResendInvite={handleResendInvite}
                      canDeactivate={canDeactivate && !!onToggleActivation}
                      isUpdatingActivation={updatingActivationIds.has(
                        member.roger_user_id ?? ''
                      )}
                      onToggleActivation={handleToggleActivation}
                      canRetryDeactivation={
                        canDeactivate && !!onRetryDeactivation
                      }
                      isRetryingDeactivation={retryingDeactivationIds.has(
                        member.roger_user_id ?? ''
                      )}
                      onRetryDeactivation={handleRetryDeactivation}
                      currentUserId={currentUserId}
                      canCancelInvite={canCancelInvite && !!onCancelInvite}
                      isCancellingInvite={cancellingInviteIds.has(
                        member.roger_user_id ?? ''
                      )}
                      onCancelInvite={handleCancelInvite}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </>
    );
  }
);
